import {Component, effect, inject, OnInit, Signal, signal} from '@angular/core';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IGlobalStockTag, SettingsReferenceEnum} from '../../models';
import {selectSelectedSetting, selectSelectedStoreStockTags} from '../../store/setting.selectors';
import {IUser} from '../../../user-access/models';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {getFieldErrorString} from '../../../../shared/utils/form/form.utils';
import {settingsSetSelected, settingStoreTagsSettings} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ICrudOptions, IUserAccessFeatureStockRules} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockRulesForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';

@Component({
  selector: 'app-feature-stock-tags',
  templateUrl: './feature-stock-tags.component.html',
  styleUrl: './feature-stock-tags.component.scss',
  standalone: false,
})
export class FeatureStockTagsComponent implements OnInit {

  //#region INJECTORS
  private formBuilder = inject(FormBuilder);
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  //#endregion

  //#region PROPERTIES
  formStockTags: FormGroup;
  protected tagsArray = signal<string[]>([]);
  protected tagsSelected = signal<string[]>([]);
  protected isTagInvalid = signal<boolean>(false);
  protected tagErrorMessage = signal<string>('');

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _stockTags: Signal<IGlobalStockTag[]> = this._store.selectSignal(selectSelectedStoreStockTags);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _uaStockRules: Signal<IUserAccessFeatureStockRules> = this._store.selectSignal(selectUserAccessFeatureStockRulesForSelectedStore);

  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const userAccess = this._uaStockRules();
      const stockTags = this._stockTags();
      this.initializeForms(userAccess, stockTags);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }

  //#endregion

  //#region ACCESSOR - STOCK TAGS SETTINGS

  get hasStockTagsBeenEdited(): boolean {
    return this.formStockTags?.dirty ?? false;
  }

  get hasStockTagsErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const tagsErrors = this.formStockTags?.controls['tags']?.errors;

    if (tagsErrors) {
      errors.push({
        errors: tagsErrors,
        error: getFieldErrorString(tagsErrors, 'tags'),
        field: 'tags',
      });
    }

    return errors;
  }

  get isStockTagsSubmitDisabled(): boolean {
    return (this.canStockTagsBeEdited === false) || (this.canStockTagsBeEdited === true && this.hasStockTagsBeenEdited === false) || (this.formStockTags?.invalid ?? true);
  }

  get canStockTagsBeEdited(): boolean {
    const access = this.stockManagerUserAccess?.stockTags;
    return access?.canEdit ?? false;
  }

  get canStockTagsBeSubmitted(): boolean {
    const access = this.stockManagerUserAccess?.stockTags;
    return access?.canSubmit ?? false;
  }

  get canStockTagsBeViewed(): boolean {
    const access = this.stockManagerUserAccess?.stockTags;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER SETTINGS + USER ACCESS + STORE
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettings(): IUser {
    return this._user();
  }

  get stockManagerUserAccess(): IUserAccessFeatureStockRules {
    return this._uaStockRules();
  }

  //#endregion

  //#region ACCESSOR - GLOBALS
  get haveAnyEditsBeenMade(): boolean {
    return (this.formStockTags?.dirty ?? false);
  }

  get stockTags(): IGlobalStockTag[] {
    return this._stockTags();
  }

  //#endregion

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.formStockTags = this.formBuilder.group({});
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertStockTagsDetails();
        break;
      case 'stockTags':
        this.revertStockTagsDetails();
        break;
    }
  }

  onSubmitForm(form: string): void {
    const selectedTagNames = this.tagsSelected();
    const stockTags = this.stockTags;

    const storeTags: IGlobalStockTag[] = selectedTagNames
      .map((tagName: string) => {
        const existingTag = stockTags.find((t: IGlobalStockTag) => t.name === tagName);
        return existingTag || {name: tagName, global: false};
      })
      .filter((tag: IGlobalStockTag) => !tag.global);

    switch (form) {
      case 'all':
      case 'stockTags':
        if (!this.canStockTagsBeSubmitted) break;
        break;
      default:
        break;
    }
    this._store.dispatch(settingStoreTagsSettings.setStoreTags({tags: storeTags}));
    this.formStockTags.markAsPristine();
    this.isTagInvalid.set(false);
    this.tagErrorMessage.set('');
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureStockRules, stockTags: IGlobalStockTag[]): void {
    this.buildStockTagsForm(access?.stockTags, stockTags);
  }

  private buildStockTagsForm(access: ICrudOptions, stockTags: IGlobalStockTag[]): void {
    if (access?.canView && this.formStockTags.pristine) {
      const tempForm = this.formBuilder.group({});

      const tagNames = stockTags.map((tag: IGlobalStockTag) => tag.name);
      this.tagsArray.set(tagNames);
      this.tagsSelected.set(tagNames);
      this.addFormControl(tempForm, 'tags', '', false, []);

      tempForm.markAsPristine();
      this.formStockTags = tempForm;
    }
  }

  private addFormControl<T = unknown>(form: FormGroup, controlName: string, value: T, disabled: boolean, validators: ValidatorFn[] = []): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  //#endregion

  //#region FORM - ON CLICK

  protected formatTagText = (text: string): string => {
    return text.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  };

  private formatAndValidateTag(tag: string): string | null {
    const formattedTag = this.formatTagText(tag);

    if (formattedTag.length === 0) {
      this.tagErrorMessage.set('Tag cannot be empty.');
      this.isTagInvalid.set(true);
      return null;
    }

    if (formattedTag.length > 15) {
      this.tagErrorMessage.set('Tag cannot exceed 15 characters.');
      this.isTagInvalid.set(true);
      return null;
    }

    const existingTags = this.tagsArray();
    if (existingTags.some((existingTag: string) => existingTag.toLowerCase() === formattedTag.toLowerCase())) {
      this.tagErrorMessage.set('Tag already exists.');
      this.isTagInvalid.set(true);
      return null;
    }

    this.tagErrorMessage.set('');
    this.isTagInvalid.set(false);
    return formattedTag;
  }

  onTagsChange(selected: { [key: string]: ISelectOption }): void {
    const allKeys = Object.keys(selected);
    const selectedKeys = allKeys.filter((key: string) => selected[key].selected);

    this.tagsArray.update((current: string[]) => {
      const newItems = allKeys.filter((key: string) => !current.includes(key));
      const validatedNewItems: string[] = [];

      for (const newItem of newItems) {
        const validatedTag = this.formatAndValidateTag(newItem);
        if (validatedTag !== null) {
          validatedNewItems.push(validatedTag);
        } else {
          return current;
        }
      }

      return [...current, ...validatedNewItems];
    });

    const validatedSelectedKeys: string[] = [];
    for (const key of selectedKeys) {
      if (this.tagsArray().includes(key)) {
        validatedSelectedKeys.push(key);
      }
    }

    this.tagsSelected.set(validatedSelectedKeys);
    this.formStockTags.markAsDirty();
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  private revertStockTagsDetails(): void {
    const stockTags = this.stockTags;
    const tagNames = stockTags.map((tag: IGlobalStockTag) => tag.name);

    this.tagsArray.set(tagNames);
    this.tagsSelected.set(tagNames);
    this.formStockTags.reset({
      tags: '',
    });
    this.isTagInvalid.set(false);
    this.tagErrorMessage.set('');
  }

  //#endregion

  //#region SELECT - OPTIONS

  getTagOptions(): { [key: string]: ISelectOption } {
    const options: { [key: string]: ISelectOption } = {};
    const tags = this.tagsArray();
    const stockTags = this.stockTags;

    tags.forEach((tag: string) => {
      const tagObj = stockTags.find((t: IGlobalStockTag) => t.name === tag);
      const isGlobal = tagObj?.global ?? false;

      options[tag] = {
        name: tag,
        selected: false,
        disabled: isGlobal,
      };
    });
    return options;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  private async onShowModalUnsavedChanges(ref: SettingsReferenceEnum): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.accountUserProfile}));
      if (result.role === 'modal-accept') {
        this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      }
    });
  }

  //#endregion

}
